import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';

import { RESOLUTION } from '@espacePro/constants/breakpoints';
import { useViewport } from '@shared/hooks/useViewport';
import Cross from '../../main-app/images/icon-close.inline.svg';
import {
  getArrowDownPositionStyles,
  getArrowLeftPositionStyles, getArrowRightPositionStyles,
  getArrowTopPositionStyles,
  getPositionStyles,
} from './helpers';

const Container = styled.div`
  position: relative;
  z-index: ${(props) => props.zIndex};

  ${(props) => props.activeZIndex && props.isOpened && `
      z-index: ${props.activeZIndex};
  `}

  .anchor {
    cursor: pointer;
  }
`;

const TooltipInstance = styled.div`
  position: absolute;
  padding: ${(props) => props.isSmall ? '12px' : '16px 32px 16px 16px'};
  background: #fff;
  box-shadow: 0 -1px 1px 0 rgba(0, 0, 0, 0.02), 0 2px 4px 0 rgba(155, 132, 135, 0.05), 0 -6px 12px 0 rgba(119, 94, 98, 0.15);
  font-size: 12px;
  border-radius: 12px;
  width: ${(props) => typeof props.width === 'number' ? `${props.width}px` : props.width};
  max-width: 70vw;
  ${(props) => getPositionStyles(props.position)}
`;

const ArrowDown = styled.span`
  width: 0;
  height: 0;
  border-left: 11px solid transparent;
  border-right: 11px solid transparent;
  border-top: 11px solid rgba(155, 132, 135, 0.06);
  bottom: -12px;
  position: absolute;

  &::before {
    content: '';
    width: 0;
    height: 0;
    border-left: 10px solid transparent;
    border-right: 10px solid transparent;
    border-top: 10px solid #fff;
    top: -12px;
    left: -10px;
    position: absolute;
  }

  ${(props) => getArrowDownPositionStyles(props.position)}
`;

const ArrowLeft = styled.span`
  width: 0;
  height: 0;
  border-top: 11px solid transparent;
  border-bottom: 11px solid transparent;
  border-right: 11px solid rgba(155, 132, 135, 0.06);
  left: -12px;
  position: absolute;

  &::before {
    content: '';
    width: 0;
    height: 0;
    border-top: 10px solid transparent;
    border-bottom: 10px solid transparent;
    border-right: 10px solid #fff;
    left: 3px;
    top: -10px;
    position: absolute;
  }

  ${(props) => getArrowLeftPositionStyles(props.position)}
`;

const ArrowTop = styled.span`
  width: 0;
  height: 0;
  border-left: 11px solid transparent;
  border-right: 11px solid transparent;
  border-bottom: 11px solid rgba(155, 132, 135, 0.06);
  top: -12px;
  position: absolute;

  &::before {
    content: '';
    width: 0;
    height: 0;
    border-left: 10px solid transparent;
    border-right: 10px solid transparent;
    border-bottom: 10px solid #fff;
    top: 2px;
    left: -10px;
    position: absolute;
  }

  ${(props) => getArrowTopPositionStyles(props.position)}
`;

const ArrowRight = styled.span`
  width: 0;
  height: 0;
  border-top: 11px solid transparent;
  border-bottom: 11px solid transparent;
  border-left: 11px solid rgba(155, 132, 135, 0.06);
  right: -11px;
  position: absolute;

  &::before {
    content: '';
    width: 0;
    height: 0;
    border-top: 10px solid transparent;
    border-bottom: 10px solid transparent;
    border-left: 10px solid #fff;
    right: 2px;
    top: -10px;
    position: absolute;
  }

  ${(props) => getArrowRightPositionStyles(props.position)}
`;

const StyledCross = styled(Cross)`
  position: absolute;
  top: 8px;
  right: 8px;
  cursor: pointer;
  width: 16px;
  height: 16px;
`;

const Tooltip = (props) => {
  const {
    children, content, trigger, width, position, positionMobile, zIndex, isSmall, activeZIndex,
  } = props;
  const [isOpened, setIsOpened] = useState(false);
  const [currentPosition, setCurrentPosition] = useState();
  const { viewportWidth } = useViewport();

  const toggleIsOpened = () => setIsOpened(!isOpened);

  const getTriggerProps = () => {
    const commonProps = {
      className: 'anchor',
    };
    if (trigger === 'click') {
      commonProps.onClick = () => toggleIsOpened();
    } else if (trigger === 'hover') {
      commonProps.onMouseEnter = () => setIsOpened(true);
      commonProps.onMouseLeave = () => setIsOpened(false);
    }
    return commonProps;
  };

  const checkCurrentPosition = (positionWeb, positionMob) => {
    if (viewportWidth > RESOLUTION.MEDIUM) {
      return positionWeb;
    }
    if (positionMob) {
      return positionMob;
    }
    return positionWeb;
  };

  useEffect(() => {
    const res = checkCurrentPosition(position, positionMobile);
    setCurrentPosition(res);
  }, []);

  return (
    <Container zIndex={zIndex} activeZIndex={activeZIndex} isOpened={isOpened}>
      {isOpened && (
      <TooltipInstance width={width} position={currentPosition} isSmall={isSmall}>
        {currentPosition === 'top left' && <ArrowDown position="left" />}
        {currentPosition === 'top center' && <ArrowDown position="center" />}
        {currentPosition === 'top right' && <ArrowDown position="right" />}
        {currentPosition === 'right top' && <ArrowLeft position="top" />}
        {currentPosition === 'right center' && <ArrowLeft position="center" />}
        {currentPosition === 'right bottom' && <ArrowLeft position="bottom" />}
        {currentPosition === 'bottom left' && <ArrowTop position="left" />}
        {currentPosition === 'bottom center' && <ArrowTop position="center" />}
        {currentPosition === 'bottom right' && <ArrowTop position="right" />}
        {currentPosition === 'left top' && <ArrowRight position="top" />}
        {currentPosition === 'left center' && <ArrowRight position="center" />}
        {currentPosition === 'left bottom' && <ArrowRight position="bottom" />}
        {trigger === 'click' && <StyledCross onClick={() => setIsOpened(false)} />}
        {content}
      </TooltipInstance>
      )}
      {React.cloneElement(children, getTriggerProps())}
    </Container>
  );
};
Tooltip.propTypes = {
  trigger: PropTypes.oneOf(['hover', 'click']),
  position: PropTypes.oneOf([
    'top left',
    'top center',
    'top right',
    'right top',
    'right center',
    'right bottom',
    'bottom left',
    'bottom center',
    'bottom right',
    'left top',
    'left center',
    'left bottom',
  ]),
  positionMobile: PropTypes.string,
  content: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
  children: PropTypes.element.isRequired,
  width: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  zIndex: PropTypes.number,
  isSmall: PropTypes.bool,
  activeZIndex: PropTypes.number,
};

Tooltip.defaultProps = {
  trigger: 'hover',
  position: 'bottom center',
  positionMobile: '',
  content: '',
  width: 250,
  zIndex: 1,
  isSmall: false,
  activeZIndex: undefined,
};

export { Tooltip };
